import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Spin, message } from 'antd';
import { api, setAuthTokenGetter, setUnauthorizedHandler } from '../services/api';
import { clearUserDetails } from '../utils/auth';

const AxiosWithAuth = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const hasStoredToken = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setAuthTokenGetter(null);
      hasStoredToken.current = false;
      return;
    }

    setAuthTokenGetter(async () => {
      try {
        return await getAccessTokenSilently();
      } catch (error) {
        console.error('Error getting access token:', error);
        return null;
      }
    });
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    setUnauthorizedHandler(async ({ isLocal: isLocalEnv }) => {
      clearUserDetails();

      if (!isLocalEnv) {
        try {
          await api.logout();
        } catch (error) {
          console.warn('Logout failed after 401, continuing with Auth0 logout:', error);
        }
      }

      logout({
        logoutParams: {
          returnTo: window.location.origin + '/',
        },
      });
    });

    return () => {
      setUnauthorizedHandler(null);
    };
  }, [logout]);

  useEffect(() => {
    const storeToken = async () => {
      if (isLoading || !isAuthenticated || hasStoredToken.current) return;

      setIsBootstrapping(true);
      try {
        const accessToken = await getAccessTokenSilently();
        await api.storeTokens(accessToken);
        hasStoredToken.current = true;
      } catch (error) {
        console.error('Error storing token after login:', error);
        message.error('Failed to complete sign in. Please try again.');
      } finally {
        setIsBootstrapping(false);
      }
    };

    storeToken();
  }, [getAccessTokenSilently, isAuthenticated, isLoading]);

  if (isLoading || (isAuthenticated && isBootstrapping)) {
    return (
      <div className="min-h-screen bg-gradient-to-l from-blue-50 to-blue-100 flex items-center justify-center">
        <Spin size="large" tip="Signing you in..." />
      </div>
    );
  }

  return children;
};

export default AxiosWithAuth;
